/**
 * Centralized API client factory.
 *
 * Every service that calls the Fastify API should use this factory instead
 * of creating its own axios instance. This ensures consistent behavior:
 *   - Bearer token (Firebase ID token) on every request
 *   - App Check token (reCAPTCHA Enterprise attestation) on every request
 *   - Vercel deployment protection bypass header
 *   - Session cookie support (`withCredentials`)
 *   - Automatic 401 retry with token + session refresh
 *   - Response unwrapping (`response.data`)
 *   - Standardized error extraction
 */

import axios, { type AxiosInstance, type AxiosRequestConfig } from 'axios';
import { foldLocationResponseNames } from '@order-up/common';
import { getApiUrl, getAutomationBypassSecret } from '../utils/env';
import { getAppCheckToken } from '../firebase/appCheck';
import { getCurrentUserToken } from './auth';
import { deactivateGuestSession, getGuestSessionBearer } from './guestSession';
import { isSessionExpiredError, refreshSessionCookie } from '../utils/sessionRefresh';
import { hasLiveImpersonatorClaim } from '../utils/impersonationClaims';
import {
    DEPLOYMENT_PROTECTED_CODE,
    isDeploymentProtectionResponse,
} from '../utils/deploymentProtection';
import { createLogger } from '../utils/logger';
import { requestMfaStepUp } from './mfaStepUpBroker';
import { requestMfaEnrollment } from './mfaEnrollmentBroker';
import { requestRecentAuth } from './recentAuthBroker';

const logger = createLogger('ApiClient');

/**
 * Create an axios instance with all standard interceptors.
 *
 * NOTE on return type: the response interceptor below replaces every
 * successful response with `response.data`, so this returns an
 * {@link ApiClient}, whose verbs resolve {@link ApiResponseBody}`<T>` — the
 * body — rather than axios's `AxiosResponse<T>`, which was one level wrong.
 * An untyped call still has an `any` payload (≈1,500 call sites rely on it);
 * `scripts/ops/guards/check-api-envelope-overread.mjs` refuses reading one
 * level past it. New code should type the payload, or call through
 * {@link getApi} / {@link postApi} / {@link putApi} below.
 *
 * @param basePath - URL path appended to the API base URL (e.g. `/api/v1/devices`).
 *                   If an absolute URL is provided it is used as-is.
 */
/**
 * Toggle the `X-OrderUp-Act-As-Impersonation` header from a token's claims.
 * Set when the live Firebase session is an impersonation/support-scope session
 * (so the API prefers this Bearer over the shared `.letsorderup.com` cookie);
 * cleared otherwise. The header is inert server-side unless the verified token
 * genuinely carries an `impersonator` claim.
 *
 * EXPIRED scopes are excluded deliberately. The header does one thing: it asks
 * the API to prefer this Bearer over cookie-first auth. Sending it with a scope
 * we can already see has lapsed asks the server to authenticate us with the one
 * credential it is guaranteed to reject (`impersonator.exp` is enforced in
 * `apps/api/components/plugins/auth.ts` independently of the carrier), so every
 * request 401s — including the redeem that would have started a FRESH support
 * session, which is how a stale session bricked the next one. Dropping the
 * header falls through to the admin's ordinary cookie, which still works.
 *
 * This weakens nothing: without the header the API may still authenticate the
 * same Bearer when no cookie is present, and the expiry check runs either way.
 */
function applyActAsImpersonationHeader(
    headers: any,
    token: { claims?: Record<string, unknown> } | null,
): void {
    if (!headers) return;
    if (hasLiveImpersonatorClaim(token)) {
        headers['X-OrderUp-Act-As-Impersonation'] = '1';
    } else {
        delete headers['X-OrderUp-Act-As-Impersonation'];
    }
}

/**
 * Who wrote a request's `Authorization`, kept on the axios config alongside the
 * `_retry` flag the 401 path already sets the same way. See the request
 * interceptor for why the difference matters.
 */
/**
 * Set ONLY by {@link getBlobApi}/{@link postBlobApi}: the response interceptor
 * returns the axios response untouched instead of unwrapping it to the body.
 */
interface RawResponseRequest {
    _orderUpRawResponse?: boolean;
}

/**
 * Set by a request the PERSON did not ask for (a best-effort background sync).
 * A `STALE_AUTH_REAUTH_REQUIRED` refusal of it is returned to the caller
 * instead of opening the "Confirm it's you" prompt — otherwise a page load
 * asks someone to re-authenticate for something they never did (prod, billing,
 * 2026-11-02: `POST /auth/sync-verified-emails` on boot opened the dialog).
 */
export interface NoRecentAuthPromptRequest {
    _noRecentAuthPrompt?: boolean;
}

interface AuthorizationProvenance {
    /** The caller set `Authorization` itself, so the interceptors leave it alone. */
    _callerAuthorization?: boolean;
    /** The interceptor wrote `Authorization` from this browser's Firebase session. */
    _sessionAuthorization?: boolean;
    /**
     * The interceptor wrote `Authorization` from this page's guest session (no
     * Firebase user: App Check refused this browser — services/guestSession.ts).
     */
    _guestAuthorization?: boolean;
}

/**
 * What an {@link ApiClient} call RESOLVES with: the JSON body, which on every
 * OrderUp route is the `{ success, data }` envelope. `T` is the PAYLOAD —
 * `body.data` — never the envelope.
 *
 * This is the type that makes the one-level-too-deep read visible. Axios's own
 * typing claims `.get()` resolves an `AxiosResponse<T>`, whose `.data` is the
 * body, so `response.data.data` LOOKED right while the interceptor below had
 * already removed that level and it resolved `undefined`. It shipped at least
 * four times (`invitations.ts` 2026-06-24, `staffIdentity.ts`, `mirrorApi.ts`,
 * `supportSlaApi.ts` 2026-10-01) with a comment warning about it in this file.
 *
 * Keeping `T` as the payload is what keeps every existing `apiClient.get<P>()`
 * caller compiling with the SAME meaning it had (`.data` is `P`). There is no
 * index signature on purpose: reading `status`, `headers` or a payload field
 * off the body is a wrong-level read too, and the compiler now says so.
 */
export interface ApiResponseBody<T = any> {
    success: boolean;
    data: T;
}

/** `any` defeats every conditional below, so it is tested for first. */
type IsAny<T> = 0 extends 1 & T ? true : false;

/** The message the compiler prints when a call names the envelope as its payload. */
export type EnvelopeTypeArgumentError =
    'apiClient type argument is the { success, data } ENVELOPE: the interceptor already unwrapped the axios response, so the type argument is the PAYLOAD (read body.data) - or call getApi<Envelope>(client, url)';

/**
 * The URL parameter of an {@link ApiClient} verb. A plain string — unless the
 * type argument is itself envelope-shaped (`success` + `data`), in which case it
 * becomes {@link EnvelopeTypeArgumentError} and the call does not compile.
 * `apiClient.patch<ApiResponse<X>>(url)` then `res.data.data` is exactly the
 * shape that shipped in `adminAdvertisingRequests.updateStatus`: typed, green,
 * and `undefined` at runtime.
 */
export type ApiUrl<T> = IsAny<T> extends true
    ? string
    : [T] extends [{ success: unknown; data: unknown }]
      ? EnvelopeTypeArgumentError
      : string;

/**
 * The interceptor-wrapped axios instance {@link createApiClient} returns. Same
 * object as before; only the verbs' declared results changed, from axios's
 * `AxiosResponse<T>` (a lie — that level was removed) to
 * {@link ApiResponseBody}`<T>`, which is what actually arrives.
 *
 * There is deliberately NO second type parameter. Axios's `R` is inferred from
 * CONTEXT, so `(): Promise<{ rows }> => apiClient.get(url)` silently declared the
 * body to be the payload — the too-SHALLOW twin of `.data.data` (it is how
 * `adminApi.getDeletePreview` read `soleOwnerTenants` off the envelope). A route
 * that answers a bare object instead of the envelope (`/reports/sales`) is
 * called through {@link getApi}`<Shape>`, which names the body explicitly.
 */
export interface ApiClient
    extends Omit<
        AxiosInstance,
        'get' | 'delete' | 'head' | 'options' | 'post' | 'put' | 'patch' | 'request'
    > {
    get<T = any>(url: ApiUrl<T>, config?: AxiosRequestConfig): Promise<ApiResponseBody<T>>;
    delete<T = any>(url: ApiUrl<T>, config?: AxiosRequestConfig): Promise<ApiResponseBody<T>>;
    head<T = any>(url: ApiUrl<T>, config?: AxiosRequestConfig): Promise<ApiResponseBody<T>>;
    options<T = any>(url: ApiUrl<T>, config?: AxiosRequestConfig): Promise<ApiResponseBody<T>>;
    post<T = any>(
        url: ApiUrl<T>,
        data?: unknown,
        config?: AxiosRequestConfig,
    ): Promise<ApiResponseBody<T>>;
    put<T = any>(
        url: ApiUrl<T>,
        data?: unknown,
        config?: AxiosRequestConfig,
    ): Promise<ApiResponseBody<T>>;
    patch<T = any>(
        url: ApiUrl<T>,
        data?: unknown,
        config?: AxiosRequestConfig,
    ): Promise<ApiResponseBody<T>>;
    request<T = any>(config: AxiosRequestConfig): Promise<ApiResponseBody<T>>;
}

/**
 * Error codes the API sends ONLY from a gate (a Fastify preHandler) that could
 * not reach its backing store — answered with `503` before the route's handler
 * runs. Nothing has executed when one of these comes back, so the request is
 * replayed, POST included, after the response's `Retry-After` (or a short
 * backoff), up to {@link MAX_GATE_REPLAYS} times.
 *
 * Add a code here only if no handler can ever send it: replaying a 503 that a
 * handler sent after doing work could write twice. The API suite holds each
 * code to that (`apps/api/test/preHandlers/gateRefusalCodes.test.ts`).
 */
export const GATE_REFUSAL_CODES: ReadonlySet<string> = new Set([
    'BOOKS_ACCESS_CHECK_UNAVAILABLE',
]);

/** Replays of one request after a gate refusal, on top of the first attempt. */
export const MAX_GATE_REPLAYS = 2;
/** Upper bound on one wait, whatever `Retry-After` asks for. */
const MAX_GATE_REPLAY_DELAY_MS = 5_000;

/** Milliseconds to wait before gate replay number `replay` (1-based). */
export function gateReplayDelayMs(retryAfter: unknown, replay: number): number {
    const raw = typeof retryAfter === 'string' ? retryAfter.trim() : '';
    // A bare number of seconds; an HTTP-date or anything else falls back to backoff.
    const seconds = raw ? Number(raw) : NaN;
    const asked = Number.isFinite(seconds) && seconds >= 0 ? seconds * 1_000 : 1_000 * 2 ** (replay - 1);
    return Math.min(asked, MAX_GATE_REPLAY_DELAY_MS);
}

/** `{ success: true, data: { dualControl: { status: 'pending_approval', … } } }` — see the response interceptor. */
function isDualControlPendingBody(body: unknown): boolean {
    if (body === null || typeof body !== 'object') return false;
    const data = (body as { data?: unknown }).data;
    if (data === null || typeof data !== 'object') return false;
    const dc = (data as { dualControl?: unknown }).dualControl;
    return dc !== null && typeof dc === 'object' && (dc as { status?: unknown }).status === 'pending_approval';
}

export function createApiClient(basePath: string): ApiClient {
    const apiUrl = getApiUrl();
    const baseURL = basePath.startsWith('http')
        ? basePath
        : apiUrl
            ? `${apiUrl}${basePath}`
            : `http://localhost:3002${basePath}`;

    const client = axios.create({
        baseURL,
        // Backstop against an indefinitely-hanging request. Axios defaults to
        // `timeout: 0` (no timeout), which let a stalled `/auth/signout` hang
        // sign-out for the full OS/proxy socket window (~1–2 min). This cap is
        // deliberately generous — nothing a browser legitimately blocks on
        // should approach it — so it never trips a real operation; user-facing
        // critical paths (e.g. sign-out) pass their own shorter per-request
        // `timeout` on top of this.
        timeout: 120_000,
        headers: {
            'Content-Type': 'application/json',
            ...(getAutomationBypassSecret() && {
                'x-vercel-protection-bypass': getAutomationBypassSecret(),
            }),
        },
        withCredentials: true,
    });

    // ─── Request: attach Bearer token + App Check token ─────────────────
    // Both helpers are independent and both swallow their own errors
    // (returning null on failure), so we run them in parallel rather than
    // stacking the App Check reCAPTCHA Enterprise challenge behind the ID
    // token fetch. Saves the larger of the two on every API call, which
    // matters most for the cold-path /auth/signin during the email-verify
    // → onboarding handoff.
    //
    // A caller that brings its OWN credential keeps it. Overwriting an explicit
    // `Authorization` with whatever Firebase session this origin happens to
    // hold would authenticate as the wrong principal. (Introduced 2026-09-11 for
    // the POS keyed-entry page, which no longer uses this client at all — the
    // till mints its session natively since 2026-09-27 — but the rule is
    // general and stays.) `_sessionAuthorization` marks a header WE wrote,
    // so a replay of our own request (401 retry, MFA step-up) still re-reads
    // the session exactly as before; only a header the caller set is kept.
    client.interceptors.request.use(
        async (config) => {
            const tracked = config as typeof config & AuthorizationProvenance;
            const callerAuthorization =
                Boolean(config.headers.Authorization) &&
                !tracked._sessionAuthorization &&
                !tracked._guestAuthorization;
            tracked._callerAuthorization = callerAuthorization;

            const [token, appCheckToken] = await Promise.all([
                callerAuthorization ? Promise.resolve(null) : getCurrentUserToken(),
                getAppCheckToken(),
            ]);

            if (token) {
                tracked._sessionAuthorization = true;
                config.headers.Authorization = `Bearer ${token.token}`;
                // Support-scope: when the CURRENT Firebase session is an
                // impersonation/support-scope session, ask the API to prefer
                // this Bearer over the shared `.letsorderup.com` cookie (which
                // still carries the admin's NORMAL identity).
                applyActAsImpersonationHeader(config.headers, token);
            }

            // No Firebase user, but this page runs on a guest session: the
            // storefront guest whose browser App Check refused. The API admits
            // it only on the guest ordering routes. A Firebase user, when there
            // is one, always wins (it is the same uid).
            if (!token && !callerAuthorization) {
                const guestBearer = getGuestSessionBearer();
                if (guestBearer) {
                    tracked._guestAuthorization = true;
                    config.headers.Authorization = `Bearer ${guestBearer}`;
                }
            }

            if (appCheckToken) {
                config.headers['X-Firebase-AppCheck'] = appCheckToken;
            }

            return config;
        },
        (error) => Promise.reject(error),
    );

    // ─── Response: unwrap data + 401 retry with refresh ─────────────────
    client.interceptors.response.use(
        (response) => {
            // A 2xx WHOSE BODY IS NOT JSON. Unwrapping to `response.data` throws
            // away the status, the headers and the URL — so by the time a caller
            // notices the body is wrong, everything that would identify WHY is
            // gone, and the only way to see it is to ask a human to open the
            // Network tab. This is the one place that still has all of it.
            //
            // Every API in this app returns a JSON envelope on success. A 2xx
            // that is a string is therefore always something OTHER than our
            // server answering: a Vercel SSO interstitial (302 followed to an
            // HTML login page), a platform timeout page, an empty body from an
            // invocation that ended early, or a truncated payload. Those look
            // identical to the caller and have entirely different fixes; the
            // status + content-type + length tell them apart immediately.
            //
            // Logged, never thrown — the caller's own handling is unchanged, and
            // a response that is legitimately non-JSON (there are none today,
            // but a future file download would be one) must not start failing
            // because of a diagnostic.
            const body: unknown = response.data;
            if (body === null || body === undefined || typeof body !== 'object') {
                const raw = typeof body === 'string' ? body : String(body);
                // FLATTENED INTO THE MESSAGE ON PURPOSE. The first version passed
                // these as an object, which `ClientLogger.error(message, error?,
                // metadata?)` put in the ERROR slot — so the browser console
                // printed a collapsed `Error: Object` and the one field that
                // actually discriminates (the status) stayed invisible unless a
                // human clicked to expand it. A diagnostic that needs a click is
                // a diagnostic that gets pasted without the answer in it.
                const detail =
                    `status=${response.status} ` +
                    `method=${response.config?.method?.toUpperCase() ?? '?'} ` +
                    `url=${response.config?.url ?? '?'} ` +
                    `contentType=${String(response.headers?.['content-type'] ?? 'none')} ` +
                    `contentLength=${String(response.headers?.['content-length'] ?? 'none')} ` +
                    `bodyType=${typeof body} bodyLength=${raw.length} ` +
                    `bodyStart=${raw.length === 0 ? '<empty>' : JSON.stringify(raw.slice(0, 200))}`;
                logger.error(
                    `API returned a 2xx whose body is not a JSON envelope — ${detail}`,
                );
            }
            // Billing dual control (SOC 2 R19): a money route that HELD the
            // action for a second approver answers 202 with
            // `data.dualControl`. Every caller of those routes parses the
            // action's own success payload, so passing this through would read
            // a pending request as "fee waived" / "plan changed". Raise it as
            // the `BILLING_APPROVAL_PENDING` refusal instead: nothing happened,
            // the catalog copy says so, and `isBillingApprovalPending` lets a
            // caller show it as information rather than as a failure.
            if (response.status === 202 && isDualControlPendingBody(body)) {
                const pending = (body as { data: { dualControl: Record<string, unknown> } }).data.dualControl;
                const held: Error & { code?: string; status?: number; dualControl?: Record<string, unknown> } = new Error(
                    'BILLING_APPROVAL_PENDING: Sent for approval — nothing has happened yet.',
                );
                held.code = 'BILLING_APPROVAL_PENDING';
                held.status = 202;
                held.dualControl = pending;
                return Promise.reject(held);
            }
            // Location rename (Phase 3, Task 26): the API may answer with
            // `locationGuid`/`locationDocId`; every web caller reads the legacy
            // names, and a body spread into a Firestore write must never store
            // the new key. This is the web's API ingress, so the fold is here:
            // the legacy name takes the new value and the new key is deleted.
            // A download that needs the RESPONSE (its headers name the file)
            // asks for it explicitly through getBlobApi/postBlobApi. Every other
            // call keeps the unwrapped body — including bare `responseType:
            // 'blob'` calls, which existing callers read as the Blob itself.
            if ((response.config as RawResponseRequest | undefined)?._orderUpRawResponse) {
                return response;
            }
            return foldLocationResponseNames(response.data);
        },
        async (error) => {
            const originalRequest = error.config;

            // Deployment-protection / proxy auth wall (e.g. Vercel Deployment
            // Protection on a preview/staging alias). The request never reached
            // Fastify, so token/session refresh is futile and the body is HTML,
            // not our JSON envelope. Short-circuit to the stable
            // `DEPLOYMENT_PROTECTED` code BEFORE the refresh/retry branch so it
            // never masquerades as a dead session (which sent operators into the
            // "sign out and back in" dead-end). See ../utils/deploymentProtection.
            if (isDeploymentProtectionResponse(error)) {
                logger.error('API blocked by deployment protection (non-JSON 401/403 wall)', {
                    url: originalRequest?.url,
                    status: error.response?.status,
                });
                const wall: Error & { code?: string; status?: number } = new Error(
                    `${DEPLOYMENT_PROTECTED_CODE}: The API is behind deployment protection and could not be reached.`,
                );
                wall.code = DEPLOYMENT_PROTECTED_CODE;
                if (typeof error.response?.status === 'number') wall.status = error.response.status;
                return Promise.reject(wall);
            }

            // A gate refused the request BEFORE its handler ran (see
            // `GATE_REFUSAL_CODES`): replay it. On 2026-10-24 a
            // single failed billing read behind the Books access gate turned an
            // HQ statement import into an error toast; nothing had been written,
            // so the one correct response was to try again.
            const gateCode = error.response?.status === 503 ? error.response?.data?.error?.code : undefined;
            if (
                originalRequest &&
                typeof gateCode === 'string' &&
                GATE_REFUSAL_CODES.has(gateCode) &&
                (originalRequest._gateReplays ?? 0) < MAX_GATE_REPLAYS
            ) {
                originalRequest._gateReplays = (originalRequest._gateReplays ?? 0) + 1;
                const delayMs = gateReplayDelayMs(
                    error.response?.headers?.['retry-after'],
                    originalRequest._gateReplays,
                );
                logger.warn(
                    `Gate refused ${originalRequest.method?.toUpperCase() ?? '?'} ${originalRequest.url ?? '?'} ` +
                        `with ${gateCode}; replay ${originalRequest._gateReplays}/${MAX_GATE_REPLAYS} in ${delayMs} ms`,
                );
                await new Promise((resolve) => setTimeout(resolve, delayMs));
                return client.request(originalRequest);
            }

            // A 401 on a CALLER-supplied credential is the caller's to handle.
            // Everything in this block recovers the browser's OWN session —
            // refreshing it, retrying as it, or navigating to the auth app —
            // and none of that is correct for a credential this client did not
            // issue: it would retry as a different principal.
            // A 401 on a guest session cannot be repaired by refreshing a
            // Firebase session the page does not have. An expired session is
            // dropped so the next page load mints a fresh guest.
            if (error.response?.status === 401 && originalRequest?._guestAuthorization) {
                if (error.response?.data?.error?.code === 'GUEST_SESSION_EXPIRED') deactivateGuestSession();
                return Promise.reject(error);
            }

            // `MFA_ENROLLMENT_REQUIRED` (403): this account holds NO second
            // factor and the route requires one — an OrderUp-staff route under
            // `STAFF_MFA_POLICY`, or the vault under `hard`. Offer enrolment
            // (in an app that mounted `MfaEnrollmentProvider` — the staff
            // consoles), then replay the ORIGINAL request once. Completing an
            // enrolment grants the step-up cookie, so the replay passes under
            // `enforce` as well as `enroll`; every gate that sends this code
            // refuses before the handler runs, so the replay is not a second
            // write. Its own flag, like the recent-auth replay, so a replay
            // can still run the MFA challenge below. Put off, or no flow
            // mounted: fall through, and the caller sees the code intact.
            if (
                error.response?.status === 403 &&
                error.response?.data?.error?.code === 'MFA_ENROLLMENT_REQUIRED' &&
                originalRequest &&
                !originalRequest._mfaEnrollmentReplayed &&
                !originalRequest._callerAuthorization &&
                !originalRequest._guestAuthorization
            ) {
                originalRequest._mfaEnrollmentReplayed = true;
                const enrolled = await requestMfaEnrollment();
                if (enrolled) {
                    return client.request(originalRequest);
                }
            }

            // `STALE_AUTH_REAUTH_REQUIRED`: the session is valid but its sign-in
            // is older than the route's `requireRecentAuth(N)` window. Only a
            // genuine re-authentication moves `auth_time`, so ask the person
            // (when this app mounted `RecentAuthProvider`), then replay the
            // ORIGINAL request once. The gate is a preHandler — it refuses
            // before the handler runs — so the replay is not a second write.
            // `finalizeReauth` has already re-minted the session cookie and
            // forced a fresh ID token, and the request interceptor re-reads the
            // session token on the replay, so both credentials carry the new
            // `auth_time`.
            //
            // Its own flag rather than `_retry`: a route can refuse for want of
            // recent sign-in AND a second factor (the vault, payroll), and the
            // replay must still be allowed to run the MFA challenge below. A
            // second stale refusal after a successful re-auth falls through to
            // the caller rather than prompting again.
            if (
                error.response?.status === 401 &&
                error.response?.data?.error?.code === 'STALE_AUTH_REAUTH_REQUIRED' &&
                originalRequest &&
                !originalRequest._recentAuthReplayed &&
                !originalRequest._noRecentAuthPrompt &&
                !originalRequest._callerAuthorization
            ) {
                originalRequest._recentAuthReplayed = true;
                const maxAgeMinutes = error.response.data.error.details?.maxAgeMinutes;
                const reauthenticated = await requestRecentAuth(
                    typeof maxAgeMinutes === 'number' ? { maxAgeMinutes } : {},
                );
                if (reauthenticated) {
                    return client.request(originalRequest);
                }
                // Declined, or no prompt mounted in this app: fall through so
                // the caller sees the real reason it was refused.
            }

            if (
                error.response?.status === 401 &&
                !originalRequest._retry &&
                !originalRequest._callerAuthorization &&
                !originalRequest.url?.includes('/auth/signout')
            ) {
                // Code-aware retry skip: some 401 codes describe states
                // that token refresh cannot fix. Auto-retrying would just
                // produce a second 401 and may strip the structured
                // envelope on the second pass (giving callers an
                // unhelpful "Request failed with status code 401").
                //
                // - `STALE_AUTH_REAUTH_REQUIRED`: Firebase doesn't update
                //   the `auth_time` claim on token refresh — only on a
                //   genuine reauthentication. The branch above already asked
                //   for one (or the app answers it per mutation, as admin's
                //   `useDestructiveMutation` does); a token-refresh retry here
                //   is guaranteed to fail again.
                const irrecoverableCode = error.response?.data?.error?.code;

                // `MFA_REAUTH_REQUIRED`: the session holds a valid, FRESH
                // token and is refused anyway, because the account has a
                // second factor this session has not satisfied. A token
                // refresh cannot produce a step-up — only completing a
                // challenge can — so the generic retry below is guaranteed to
                // fail a second time.
                //
                // Instead: run the challenge, then replay the ORIGINAL request
                // once. The step-up cookie is httpOnly and set by the verify
                // endpoint, so the replay carries it without this code ever
                // touching a credential. `_retry` is set first so a replay that
                // is refused again falls through to the caller rather than
                // looping.
                if (irrecoverableCode === 'MFA_REAUTH_REQUIRED' && !originalRequest._retry) {
                    originalRequest._retry = true;
                    const steppedUp = await requestMfaStepUp();
                    if (steppedUp) {
                        return client.request(originalRequest);
                    }
                    // Declined, or no UI registered to ask. Fall through so the
                    // caller sees the real reason it was refused.
                }

                if (
                    irrecoverableCode === 'STALE_AUTH_REAUTH_REQUIRED' ||
                    irrecoverableCode === 'MFA_REAUTH_REQUIRED' ||
                    irrecoverableCode === 'MFA_ENROLLMENT_REQUIRED'
                ) {
                    // Fall through to the formatting block below so the
                    // structured envelope reaches the caller intact.
                } else {
                originalRequest._retry = true;

                try {
                    if (isSessionExpiredError(error)) {
                        const sessionRefreshed = await refreshSessionCookie();
                        if (sessionRefreshed) {
                            const freshToken = await getCurrentUserToken(true);
                            if (freshToken) {
                                originalRequest._sessionAuthorization = true;
                                originalRequest.headers.Authorization = `Bearer ${freshToken.token}`;
                                applyActAsImpersonationHeader(originalRequest.headers, freshToken);
                            }
                            return client.request(originalRequest);
                        }
                    } else {
                        const freshToken = await getCurrentUserToken(true);
                        if (freshToken) {
                            originalRequest._sessionAuthorization = true;
                            originalRequest.headers.Authorization = `Bearer ${freshToken.token}`;
                            applyActAsImpersonationHeader(originalRequest.headers, freshToken);
                            return client.request(originalRequest);
                        }
                    }
                } catch (refreshError) {
                    logger.error('Token/session refresh failed', {
                        error: refreshError instanceof Error
                            ? refreshError.message
                            : String(refreshError),
                    });

                    // All refresh attempts exhausted — redirect to auth app.
                    // This handles mid-session revocation (user's assignments revoked,
                    // tokens revoked, refresh also fails).
                    if (typeof window !== 'undefined') {
                        const authUrl = process.env.NEXT_PUBLIC_AUTH_URL || '/';
                        window.location.href = `${authUrl}?reason=session_expired`;
                        return new Promise(() => {}); // Never resolves — page is navigating
                    }
                }
                } // close STALE_AUTH-skip else
            }

            if (axios.isAxiosError(error) && error.response) {
                // Encode the error code into the message string so consumers
                // can branch on it via simple `message.includes(...)` checks
                // without each one parsing axios response shapes. Common
                // patterns:
                //   - `AUTH_SESSION_INVALID: Your session is no longer valid...`
                //     (ghost-session — useUserSessionValidation clears cache)
                //   - `BILLING_NOT_CONFIGURED: ...`
                //   - bare message when no code is present
                //
                // Also attach the raw `code`, `details`, and HTTP `status` as
                // fields on the rejected Error so callers that need structured
                // payloads (e.g. `LAST_OWNER_BLOCKED.details.affectedTenants`,
                // `STALE_AUTH_REAUTH_REQUIRED.details.maxAgeMinutes`) can read
                // them without re-parsing the message string.
                const code = error.response.data?.error?.code;
                const details = error.response.data?.error?.details;
                // Field-level validation payload. Some routes (e.g. payroll)
                // emit `error.fields: [{ path, message }]` for form-level
                // mapping; carry it through so typed consumers
                // (`PayrollValidationError`) can re-hydrate it without re-parsing
                // the axios response, mirroring `details` above.
                const fields = error.response.data?.error?.fields;
                const status = error.response.status;
                // Surface the request correlation id when present so the UI can
                // show the customer a support reference. The API emits it three
                // ways: the `error.correlationId` field on failures, the
                // `meta.correlationId` field on every response, and the
                // `X-Correlation-ID` response header (set globally by the
                // logging plugin + exposed via CORS). The header is the only
                // source for routes whose catch block writes a bare envelope
                // without the id (e.g. Books validation 400s), so it's the
                // final fallback.
                const headerCorrelationId =
                    (error.response.headers?.['x-correlation-id'] as
                        | string
                        | undefined) ??
                    (error.response.headers?.['X-Correlation-ID'] as
                        | string
                        | undefined);
                const correlationId =
                    error.response.data?.error?.correlationId ??
                    error.response.data?.meta?.correlationId ??
                    headerCorrelationId;
                const baseMessage =
                    error.response.data?.error?.message || error.message;
                const errorMessage = code
                    ? `${code}: ${baseMessage}`
                    : baseMessage;
                const wrapped: Error & {
                    code?: string;
                    details?: unknown;
                    fields?: unknown;
                    status?: number;
                    correlationId?: string;
                } = new Error(errorMessage);
                if (code) wrapped.code = code;
                if (details !== undefined) wrapped.details = details;
                if (fields !== undefined) wrapped.fields = fields;
                if (typeof status === 'number') wrapped.status = status;
                if (correlationId) wrapped.correlationId = correlationId;
                return Promise.reject(wrapped);
            }
            return Promise.reject(error);
        },
    );

    // The interceptor above changed what every verb resolves with; say so.
    return client as unknown as ApiClient;
}

// ─── Typed helpers for new code ───────────────────────────────────────────────
//
// These wrap an apiClient call and declare the unwrapped envelope type
// honestly, so consumers don't need `as unknown as Envelope<T>` casts.
// Old code can keep calling `apiClient.get(...)` directly; new code
// should prefer these.

/**
 * The JSON envelope every OrderUp API route returns on success.
 *
 * READ THIS BEFORE WRITING `response.data.data`. The response interceptor above
 * already replaces the axios response with `response.data`, so the value you
 * await from an apiClient call IS this envelope — `.data` is the payload and
 * `.data.data` is one level too deep. That mistake shipped in
 * `invitationService.acceptInvitation` on 2026-06-24 when the call moved from
 * raw axios to `apiClient` and the unwrap was not adjusted: it returned
 * `undefined`, `useAcceptInvitation.onSuccess` dereferenced it, and the
 * resulting TypeError turned every successful invitation accept into a failed
 * mutation with a generic error toast for six weeks.
 *
 * Raw `axios` calls in the same files are NOT affected — there `response` is a
 * real AxiosResponse and `response.data.data` is correct. The two shapes look
 * identical at the call site, which is exactly why this type exists: name the
 * envelope and the ambiguity goes away.
 */
export interface ApiEnvelope<T> {
  success: boolean;
  data: T;
  meta?: Record<string, unknown>;
}

/** GET that returns the unwrapped response body typed as T. */
export async function getApi<T>(
  client: ApiClient | AxiosInstance,
  url: string,
  config?: AxiosRequestConfig,
): Promise<T> {
  // The interceptor replaces the response with response.data, so axios's
  // declared `AxiosResponse<T>` return is actually T at runtime.
  return (client as AxiosInstance).get(url, config) as unknown as Promise<T>;
}

/** POST that returns the unwrapped response body typed as T. */
export async function postApi<T>(
  client: ApiClient | AxiosInstance,
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig,
): Promise<T> {
  return (client as AxiosInstance).post(url, data, config) as unknown as Promise<T>;
}

/** PUT that returns the unwrapped response body typed as T. */
export async function putApi<T>(
  client: ApiClient | AxiosInstance,
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig,
): Promise<T> {
  return (client as AxiosInstance).put(url, data, config) as unknown as Promise<T>;
}

/** PATCH that returns the unwrapped response body typed as T. */
export async function patchApi<T>(
  client: ApiClient | AxiosInstance,
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig,
): Promise<T> {
  return (client as AxiosInstance).patch(url, data, config) as unknown as Promise<T>;
}

/** DELETE that returns the unwrapped response body typed as T. */
export async function deleteApi<T>(
  client: ApiClient | AxiosInstance,
  url: string,
  config?: AxiosRequestConfig,
): Promise<T> {
  return (client as AxiosInstance).delete(url, config) as unknown as Promise<T>;
}

/** A file the API streamed back, with the headers that describe it. */
export interface ApiDownload {
  blob: Blob;
  /** Lower-cased header names. Cross-origin, only CORS-exposed headers appear. */
  headers: Record<string, string | undefined>;
}

function toDownload(response: unknown): ApiDownload {
  const raw = response as { data?: unknown; headers?: unknown } | null;
  if (!raw || !(raw.data instanceof Blob)) {
    // The interceptor did not hand back the raw response, or the body is not a
    // file. Refuse here, where the cause is known, rather than resolving a
    // `{ blob: undefined }` that fails later as "Failed to execute
    // createObjectURL".
    throw new Error('API download did not return a file body');
  }
  const headers: Record<string, string | undefined> = {};
  const source = raw.headers as Record<string, unknown> | undefined;
  if (source) {
    for (const [key, value] of Object.entries(source)) {
      if (value !== undefined && value !== null) headers[key.toLowerCase()] = String(value);
    }
  }
  return { blob: raw.data, headers };
}

/**
 * GET a file. A bare `apiClient.get(url, { responseType: 'blob' })` resolves the
 * Blob ITSELF (the interceptor unwraps it like any body), so `response.data` and
 * `response.headers` on it are `undefined` — which is how the Books invoice /
 * estimate PDF, the audit export and the design export downloads broke. Use this
 * when the headers matter (Content-Disposition names the file).
 */
export async function getBlobApi(
  client: ApiClient | AxiosInstance,
  url: string,
  config?: AxiosRequestConfig,
): Promise<ApiDownload> {
  const request = { ...config, responseType: 'blob', _orderUpRawResponse: true };
  return toDownload(await (client as AxiosInstance).get(url, request as AxiosRequestConfig));
}

/** POST that streams back a file. See {@link getBlobApi}. */
export async function postBlobApi(
  client: ApiClient | AxiosInstance,
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig,
): Promise<ApiDownload> {
  const request = { ...config, responseType: 'blob', _orderUpRawResponse: true };
  return toDownload(
    await (client as AxiosInstance).post(url, data, request as AxiosRequestConfig),
  );
}
